import { itemList } from '@forge42/seo-tools/structured-data/item-list'
import { organization } from '@forge42/seo-tools/structured-data/organization'

/**
 * Check if a string is a valid absolute URL
 */
function isAbsoluteUrl(url: string): boolean {
	try {
		new URL(url)
		return true
	} catch {
		return false
	}
}

/**
 * Build Organization JSON-LD for DFW Professionals
 */
export function buildOrganizationLd({ origin }: { origin: string }): string {
	return JSON.stringify(
		organization({
			'@type': 'Organization',
			/*
			 * `name` is the string we want quoted back at us. A model writing
			 * "according to DFW Professionals" produces something a reader can retype;
			 * "DFW Professionals" is longer and collides with every staffing
			 * agency in the metroplex. `alternateName` keeps the legal and
			 * historical name attached without competing for the citation, and
			 * `url` stays on the domain that holds the authority.
			 */
			name: 'DFW Professionals',
			alternateName: 'DFW Professionals',
			url: origin,
			logo: `${origin}/img/logo.png`,
			sameAs: [
				// Add social media profiles here when available
			],
		}),
	)
}

/**
 * Build Breadcrumb JSON-LD (manual implementation to avoid strict URL validation)
 */
export function buildBreadcrumbLd(
	items: Array<{ name: string; url: string }>,
): string {
	const itemListElement = items
		.filter((i) => isAbsoluteUrl(i.url))
		.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url,
		}))

	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement,
	})
}

/**
 * Build ItemList JSON-LD
 */
export function buildItemListLd({
	name,
	items,
	author,
	dateModified,
}: {
	name: string
	items: Array<{ name: string; url: string }>
	/**
	 * Who is responsible for the ordering. A reference to a Person node rather
	 * than an inline person, so one author across many rankings resolves to one
	 * entity instead of one per page.
	 */
	author?: { '@type': 'Person'; '@id': string; name: string }
	/**
	 * When the ordering last changed. Sourced from the last rescore, never from
	 * "now" — a timestamp that moves whenever a job runs is the artificial
	 * freshness signal that gets pages discounted rather than cited.
	 */
	dateModified?: string
}): string {
	return JSON.stringify(
		itemList({
			'@type': 'ItemList',
			name,
			itemListElement: items.map((item, index) => ({
				'@type': 'ListItem' as const,
				position: index + 1,
				name: item.name,
				url: item.url,
			})),
			...(author ? { author } : {}),
			...(dateModified ? { dateModified } : {}),
		} as any),
	)
}

/**
 * The description precedence for a business, used by the page body, the meta
 * description, and the JSON-LD.
 *
 * One function because these are three separate code paths that must agree.
 * They previously drifted into three different chains, so a business with only
 * aiDescription showed readers one summary and handed crawlers another.
 *
 * whyItRanks first: it is grounded in reviews we read and gated by the
 * citation rules. aiDescription is older generic copy.
 */
export function businessDescription(business: {
	whyItRanks?: string | null
	aiDescription?: string | null
	description?: string | null
}): string | null {
	return (
		business.whyItRanks ||
		business.aiDescription ||
		business.description ||
		null
	)
}

/**
 * Build LocalBusiness JSON-LD for business detail pages
 */
export function buildLocalBusinessLd(
	business: {
		name: string
		description?: string | null
		aiDescription?: string | null
		whyItRanks?: string | null
		address?: string | null
		phone?: string | null
		website?: string | null
		featuredMedia?: { url: string | null } | null
		cities: Array<{ name: string }>
		categories: Array<{ name: string }>
		subcategories: Array<{ name: string }>
	},
	canonicalUrl: string,
) {
	const schema: Record<string, any> = {
		'@context': 'https://schema.org',
		'@type': 'LocalBusiness',
		name: business.name,
		url: canonicalUrl,
	}

	// Same precedence as the page body and the meta description.
	const description = businessDescription(business)
	if (description) {
		schema.description = description
	}

	// Phone
	if (business.phone) {
		schema.telephone = business.phone
	}

	// Image
	if (business.featuredMedia?.url && business.featuredMedia.url) {
		schema.image = business.featuredMedia.url
	}

	// Address - best effort from business.address + first city
	if (business.address || business.cities.length > 0) {
		const addressSchema: Record<string, string> = {
			'@type': 'PostalAddress',
		}

		if (business.address) {
			addressSchema.streetAddress = business.address
		}

		if (business.cities.length > 0) {
			addressSchema.addressLocality = business.cities[0]!.name
			addressSchema.addressRegion = 'TX' // Dallas-Fort Worth area
			addressSchema.addressCountry = 'US'
		}

		schema.address = addressSchema
	}

	// Area served (all cities)
	if (business.cities.length > 0) {
		schema.areaServed = business.cities.map((city) => ({
			'@type': 'City',
			name: city.name,
		}))
	}

	// Knows about (categories/subcategories)
	const allCategories = [
		...business.categories.map((c) => c.name),
		...business.subcategories.map((s) => s.name),
	]
	if (allCategories.length > 0) {
		schema.knowsAbout = allCategories
	}

	// Same as (website)
	if (business.website) {
		schema.sameAs = [business.website]
	}

	return JSON.stringify(schema)
}

/**
 * Build FAQ JSON-LD from aiFaqSnippets
 * Generates standards-compliant FAQPage schema per https://schema.org/FAQPage
 */
export function buildFaqLd(faqItems: Array<{ q: string; a: string }>): string {
	const schema = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqItems.map((item) => ({
			'@type': 'Question',
			name: item.q,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.a,
			},
		})),
	}
	return JSON.stringify(schema)
}

/**
 * Build FAQ Page JSON-LD with question/answer pairs
 * Alternative interface using { question, answer } format
 */
export function buildFAQPageLd(
	faqItems: Array<{ question: string; answer: string }>,
): string {
	const schema = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqItems.map((item) => ({
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.answer,
			},
		})),
	}
	return JSON.stringify(schema)
}
